import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { X, Share2 } from "lucide-react";
import html2canvas from "html2canvas";
import { useGeo } from "@/context/GeoContext";
import { formatPrice } from "@/lib/currency";
import ExplorerAnimation from "@/components/explorer/ExplorerAnimation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tiny country-code → emoji flag util. Pure unicode magic — no asset needed.
function flagEmoji(cc) {
    if (!cc || cc.length !== 2) return "";
    const code = cc.toUpperCase();
    return String.fromCodePoint(...[...code].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}

const VIBE_CLASS =
    "inline-flex items-center rounded-full border border-[#E5E5E5] px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#737373]";

function PriceRow({ color, label, value, testid }) {
    return (
        <div className="flex items-center gap-2" data-testid={testid}>
            <span
                aria-hidden
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: color }}
                data-testid={`${testid}-dot`}
            />
            <span className="text-[12px] text-[#0A0A0A]">{label}</span>
            <span className="ml-auto text-[12px] font-semibold text-[#0A0A0A]">{value}</span>
        </div>
    );
}

function DestinationCard({ d, currency, onTap }) {
    const flag = flagEmoji(d.country);
    const fmt = (amt, ccy) => formatPrice({ amount: amt, currency: ccy || currency });
    return (
        <button
            type="button"
            onClick={() => onTap(d.city)}
            data-testid={`explorer-card-${d.iata}`}
            className="group flex w-full flex-col gap-2 rounded-2xl border border-[#E5E5E5] bg-white p-4 text-left transition hover:border-[#0A0A0A]"
        >
            <div className="flex items-center justify-between">
                <h3 className="text-[14px] font-semibold tracking-tight text-[#0A0A0A]">
                    <span aria-hidden className="mr-1.5">{flag}</span>
                    {d.city}
                </h3>
                {d.vibe && <span className={VIBE_CLASS}>{d.vibe}</span>}
            </div>
            <div className="flex flex-col gap-1">
                {d.flight?.min != null && (
                    <PriceRow
                        color="#2563EB"
                        testid={`explorer-price-flight-${d.iata}`}
                        label="Flight from"
                        value={fmt(d.flight.min, d.flight.currency)}
                    />
                )}
                {d.hotel?.min != null && (
                    <PriceRow
                        color="#16A34A"
                        testid={`explorer-price-hotel-${d.iata}`}
                        label={`Hotels / night`}
                        value={`${fmt(d.hotel.min, d.hotel.currency)}${d.hotel.max ? " – " + fmt(d.hotel.max, d.hotel.currency) : ""}`}
                    />
                )}
                {d.package?.price != null && (
                    <PriceRow
                        color="#7C3AED"
                        testid={`explorer-price-package-${d.iata}`}
                        label={`Package · ${d.package.duration_days || 4} nights`}
                        value={fmt(d.package.price, d.package.currency)}
                    />
                )}
            </div>
        </button>
    );
}

export default function ExplorerModal({ open, onClose, onPickCity }) {
    const { geo, userGeo, currencyPref } = useGeo();
    const country_code = (geo?.country_code || userGeo?.country_code || "US").toUpperCase();
    const origin_iata = userGeo?.nearest_iata || null;
    const lat = userGeo?.lat ?? null;
    const lng = userGeo?.lon ?? userGeo?.lng ?? null;
    const place_hint = userGeo?.suburb || userGeo?.locality || userGeo?.city || null;
    const currency = (currencyPref || geo?.currency || "USD").toUpperCase();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const captureRef = useRef(null);
    const fetchedKey = useRef(null);

    useEffect(() => {
        if (!open) return;
        // Phase 14.fix — render the flow chart whenever ANY origin
        // signal exists (nearest_iata, lat/lng, OR a city/locality
        // hint from IP geo). The backend `/api/explorer/auto` segment
        // accepts `place_hint` and resolves the nearest airport from
        // a city name, so we no longer need precise coordinates to
        // unlock curated routes.
        if (lat == null && lng == null && !origin_iata && !place_hint) return;
        const key = `${origin_iata || "@"}|${lat}|${lng}|${place_hint || ""}|${currency}`;
        if (fetchedKey.current === key && data) return;
        fetchedKey.current = key;
        setLoading(true);
        setErr(null);
        const params = new URLSearchParams({ currency });
        if (lat != null && lng != null) {
            params.set("lat", String(lat));
            params.set("lng", String(lng));
        }
        if (place_hint) params.set("place_hint", place_hint);
        const seg = origin_iata || "auto";
        axios.get(`${API}/explorer/${seg}?${params.toString()}`, { timeout: 20000 })
            .then(r => setData(r.data))
            .catch(e => setErr(e?.message || "failed to load"))
            .finally(() => setLoading(false));
    }, [open, origin_iata, lat, lng, currency, place_hint, data]);

    if (!open) return null;

    const resolvedOrigin = data?.origin || null;
    const pricingLabel = data?.pricing_origin_label || null;
    const displayCity = resolvedOrigin?.city || userGeo?.city || geo?.city || country_code;

    const domestic = (data?.destinations || []).filter(d => d.scope === "domestic");
    const international = (data?.destinations || []).filter(d => d.scope === "international");

    const handlePick = (city) => {
        onPickCity && onPickCity(city);
        onClose && onClose();
    };

    const handleShare = async () => {
        if (!captureRef.current) return;
        try {
            const canvas = await html2canvas(captureRef.current, {
                backgroundColor: "#FFFFFF",
                scale: 2,
                useCORS: true,
            });
            canvas.toBlob(async (blob) => {
                if (!blob) return;
                const file = new File([blob], `piqpath-${displayCity}.png`, { type: "image/png" });
                if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                    try {
                        await navigator.share({
                            files: [file],
                            title: `Piqpath — Where next from ${displayCity}?`,
                            text: `Curated destinations from ${displayCity}`,
                        });
                        return;
                    } catch { /* fall through to download */ }
                }
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `piqpath-${displayCity}.png`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            }, "image/png");
        } catch (e) {
            console.error("[explorer-share] failed", e);
        }
    };

    return (
        <div
            data-testid="explorer-modal"
            className="fixed inset-0 z-[60] flex flex-col items-center overflow-y-auto bg-white"
        >
            {/* Header bar. Phase 14.9.15 A2 — apply safe-area-inset
                padding so the close/share buttons clear the iOS notch
                / status bar on Pro / Pro Max / 15+/16+ models. */}
            <div
                className="sticky top-0 z-[61] flex w-full max-w-[820px] items-center justify-between border-b border-[#E5E5E5] bg-white/95 px-5 py-3 backdrop-blur"
                style={{
                    paddingTop: "max(0.75rem, env(safe-area-inset-top))",
                    paddingRight: "max(1.25rem, env(safe-area-inset-right))",
                    paddingLeft: "max(1.25rem, env(safe-area-inset-left))",
                }}
            >
                <span className="text-[12px] uppercase tracking-wider text-[#737373]">
                    From {displayCity} · {country_code}
                </span>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleShare}
                        data-testid="explorer-share"
                        aria-label="Share"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E5E5E5] text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                    >
                        <Share2 size={15} strokeWidth={1.75} />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="explorer-close"
                        aria-label="Close"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E5E5E5] text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                    >
                        <X size={16} strokeWidth={1.75} />
                    </button>
                </div>
            </div>

            <div ref={captureRef} className="mx-auto w-full max-w-[820px] px-5 pb-10 pt-6">
                {/* Phase 14.fix — render the vertical flow chart whenever
                    destinations are present. No map fallback, ever. */}
                {data?.destinations?.length > 0 && (
                    <ExplorerAnimation
                        countryCode={country_code}
                        destinations={data.destinations}
                        origin={data.origin || { city: displayCity }}
                        onPick={handlePick}
                    />
                )}

                {/* Phase 14.5 — "Where next?" header + popular chips only
                    surface when destinations are empty. When the flow chart
                    is up, it carries its own "FROM {origin}" identity. */}
                {!loading && !err && !data?.destinations?.length && (
                    <>
                        <h1 className="text-center text-[26px] font-semibold tracking-tight text-[#0A0A0A]">
                            Where next?
                        </h1>
                        <p className="mt-1 text-center text-[13px] text-[#737373]">
                            Curated destinations from {displayCity}
                        </p>
                        {pricingLabel && (
                            <p className="mt-1 text-center text-[11px] text-[#A3A3A3]" data-testid="pricing-origin-label">
                                Flight prices {pricingLabel}
                            </p>
                        )}
                    </>
                )}

                {loading && (
                    <div className="mt-10 text-center text-[12px] text-[#737373]" data-testid="explorer-loading">
                        Pricing curated routes…
                    </div>
                )}
                {err && (
                    <div className="mt-10 text-center text-[12px] text-[#B91C1C]">{err}</div>
                )}
                {/* Phase 14.9.15 A3 — dropped the "Popular chips"
                    empty-state block. Requirement: nothing below the
                    flow chart, to keep the mobile viewport clean. */}
            </div>
        </div>
    );
}
